"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { Search, ShoppingCart, Globe, Menu, X, User, BookOpen, FileText, Award, HeadphonesIcon, LogOut, ChevronDown, ChevronRight, Check } from "lucide-react";
import { useTranslation } from "@/contexts/TranslationContext";
import { useCart } from "@/contexts/CartContext";

interface HeaderProps {
  showSearch?: boolean;
  minimal?: boolean;
}

const languages = [
  { code: "pt", label: "Português" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
] as const;

// Simular dados do usuário logado (no futuro virá do contexto de autenticação)
interface LoggedUser {
  id: number;
  firstName: string;
  email: string;
}

export default function Header({ showSearch = true, minimal = false }: HeaderProps) {
  const { locale, setLocale, t } = useTranslation();
  const { count: cartCount } = useCart();
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [exploreMenuOpen, setExploreMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [languageSubmenuOpen, setLanguageSubmenuOpen] = useState(false);
  const [headerLogo, setHeaderLogo] = useState<string | null>(null);
  const [primaryColor, setPrimaryColor] = useState<string>("#3B82F6");

  // Simular usuário logado - no futuro virá de um contexto de autenticação
  const [loggedUser, setLoggedUser] = useState<LoggedUser | null>(null);

  const exploreRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Verificar se há usuário logado (simulação)
  useEffect(() => {
    // Simular verificação de login - no futuro será substituído por autenticação real
    const checkLoggedUser = () => {
      const savedUser = localStorage.getItem("loggedUser");
      if (savedUser) {
        try {
          setLoggedUser(JSON.parse(savedUser));
        } catch {
          setLoggedUser(null);
        }
      }
    };
    checkLoggedUser();

    // Escutar mudanças no localStorage
    window.addEventListener("storage", checkLoggedUser);
    return () => window.removeEventListener("storage", checkLoggedUser);
  }, []);

  useEffect(() => {
    const fetchHeaderData = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:5000";
        const response = await fetch(`${backendUrl}/api/website/public/logos`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          if (data.data?.header_logo) {
            setHeaderLogo(data.data.header_logo);
          }
          if (data.data?.primary_color) {
            setPrimaryColor(data.data.primary_color);
          }
        }
      } catch (error) {
        // Silenciar erro em desenvolvimento - logo fallback será exibido
        if (process.env.NODE_ENV === "development") {
          console.warn("Backend não disponível, usando logo fallback");
        }
      }
    };

    fetchHeaderData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exploreRef.current && !exploreRef.current.contains(event.target as Node)) {
        setExploreMenuOpen(false);
      }
      if (languageRef.current && !languageRef.current.contains(event.target as Node)) {
        setLanguageMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLanguageChange = (code: "pt" | "en" | "es") => {
    setLocale(code);
    setLanguageMenuOpen(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/cursos?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("loggedUser");
    setLoggedUser(null);
    setUserMenuOpen(false);
    window.location.href = "/";
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <nav className="max-w-[1400px] mx-auto px-4">
        <div className="flex items-center h-[72px] gap-4">
          {/* Logo */}
          <Link href="/" className="flex-shrink-0">
            <div className="flex items-center h-10">
              {headerLogo && (
                <Image
                  src={headerLogo}
                  alt="Logo"
                  width={120}
                  height={40}
                  className="h-10 w-auto object-contain"
                  unoptimized
                />
              )}
            </div>
          </Link>

          {/* Search Bar */}
          {showSearch && (
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-[600px]">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("header.searchPlaceholder")}
                  className="w-full h-12 pl-12 pr-4 text-base text-gray-900 bg-gray-50 border-2 border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:border-transparent placeholder-gray-500"
                  style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                />
              </div>
            </form>
          )}

          {/* Right Side Navigation - Desktop */}
          <div className="hidden lg:flex items-center gap-1 ml-auto">
            {/* Explorar */}
            <div className="relative" ref={exploreRef}>
              <button
                onClick={() => setExploreMenuOpen(!exploreMenuOpen)}
                className="text-sm text-gray-700 transition px-3 py-2 cursor-pointer hover:opacity-80"
                style={{ "--hover-color": primaryColor } as React.CSSProperties}
                onMouseEnter={(e) => (e.currentTarget.style.color = primaryColor)}
                onMouseLeave={(e) => (e.currentTarget.style.color = "")}
              >
                {t("header.explore")}
              </button>

              {exploreMenuOpen && (
                <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                  <Link
                    href="/cursos"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => setExploreMenuOpen(false)}
                  >
                    {t("header.courses")}
                  </Link>
                  <Link
                    href="/simulador"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => setExploreMenuOpen(false)}
                  >
                    {t("header.careerSimulator")}
                  </Link>
                  <Link
                    href="/verificar"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => setExploreMenuOpen(false)}
                  >
                    {t("header.verifyCertificate")}
                  </Link>
                </div>
              )}
            </div>

            {/* Cart */}
            <Link
              href="/carrinho"
              className="relative p-2 text-gray-700 transition cursor-pointer"
              onMouseEnter={(e) => (e.currentTarget.style.color = primaryColor)}
              onMouseLeave={(e) => (e.currentTarget.style.color = "")}
              aria-label={`Carrinho (${cartCount})`}
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                  style={{ backgroundColor: primaryColor }}
                >
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Auth Buttons ou User Menu */}
            {loggedUser ? (
              /* Menu do usuário logado */
              <div className="relative ml-2" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg transition cursor-pointer hover:bg-gray-100"
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${primaryColor}20` }}
                  >
                    <User className="w-5 h-5" style={{ color: primaryColor }} />
                  </div>
                  <span className="text-sm font-medium text-gray-700 hidden xl:inline">
                    Olá, {loggedUser.firstName}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                    {/* Header do menu */}
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <p className="text-sm font-semibold text-gray-900">Olá, {loggedUser.firstName}</p>
                      <p className="text-xs text-gray-500 truncate">{loggedUser.email}</p>
                    </div>

                    {/* Links do menu */}
                    <div className="py-1">
                      <Link
                        href="/meu-aprendizado"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <BookOpen className="w-4 h-4 text-gray-400" />
                        Meus Cursos
                      </Link>
                      <Link
                        href="/meus-dados"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <FileText className="w-4 h-4 text-gray-400" />
                        Meus Dados
                      </Link>
                      <Link
                        href="/meu-aprendizado?tab=certificacoes"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Award className="w-4 h-4 text-gray-400" />
                        Certificações
                      </Link>
                    </div>

                    <div className="border-t border-gray-100 py-1">
                      {/* Item Idioma com submenu */}
                      <div>
                        <button
                          onClick={() => setLanguageSubmenuOpen(!languageSubmenuOpen)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition w-full cursor-pointer"
                        >
                          <Globe className="w-4 h-4 text-gray-400" />
                          Idioma
                          <span className="ml-auto flex items-center gap-1 text-xs text-gray-400">
                            {languages.find(l => l.code === locale)?.label}
                            <ChevronRight className={`w-3 h-3 transition-transform ${languageSubmenuOpen ? "rotate-90" : ""}`} />
                          </span>
                        </button>
                        {/* Submenu de idiomas */}
                        {languageSubmenuOpen && (
                          <div className="bg-gray-50 py-1">
                            {languages.map((lang) => (
                              <button
                                key={lang.code}
                                onClick={() => {
                                  handleLanguageChange(lang.code);
                                  setLanguageSubmenuOpen(false);
                                }}
                                className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 transition cursor-pointer pl-11"
                              >
                                {locale === lang.code && (
                                  <Check className="w-4 h-4 absolute left-4" style={{ color: primaryColor }} />
                                )}
                                <span className={locale === lang.code ? "font-medium" : ""} style={locale === lang.code ? { color: primaryColor } : {}}>
                                  {lang.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Link
                        href="/meu-aprendizado?tab=suporte"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <HeadphonesIcon className="w-4 h-4 text-gray-400" />
                        Suporte
                      </Link>
                    </div>

                    <div className="border-t border-gray-100 py-1">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition w-full cursor-pointer"
                      >
                        <LogOut className="w-4 h-4" />
                        Sair da Conta
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Botões de Login e Cadastro */
              <>
                <Link
                  href="/login"
                  className="ml-2 px-4 py-2 text-sm font-bold text-gray-900 border-2 border-gray-300 rounded-md transition"
                  style={{ "--hover-border": primaryColor } as React.CSSProperties}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = primaryColor;
                    e.currentTarget.style.color = primaryColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "";
                    e.currentTarget.style.color = "";
                  }}
                >
                  {t("header.login")}
                </Link>
                <Link
                  href="/cadastro"
                  className="ml-2 px-4 py-2 text-sm font-bold text-white bg-gray-900 rounded-md hover:bg-gray-800 transition"
                >
                  {t("header.signup")}
                </Link>
              </>
            )}

            {/* Language - só mostrar quando NÃO logado */}
            {!loggedUser && (
              <div className="relative ml-2" ref={languageRef}>
                <button
                  onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                  className="p-2 border-2 border-gray-300 rounded-md transition cursor-pointer"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = primaryColor;
                    const icon = e.currentTarget.querySelector("svg");
                    if (icon) icon.style.color = primaryColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "";
                    const icon = e.currentTarget.querySelector("svg");
                    if (icon) icon.style.color = "";
                  }}
                >
                  <Globe className="h-5 w-5 text-gray-900 transition" />
                </button>

                {languageMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className="w-full text-left px-4 py-2 text-sm transition text-gray-700"
                        style={
                          locale === lang.code
                            ? { color: primaryColor, fontWeight: 600, backgroundColor: `${primaryColor}15` }
                            : {}
                        }
                        onMouseEnter={(e) => {
                          if (locale !== lang.code) {
                            e.currentTarget.style.backgroundColor = `${primaryColor}10`;
                            e.currentTarget.style.color = primaryColor;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (locale !== lang.code) {
                            e.currentTarget.style.backgroundColor = "";
                            e.currentTarget.style.color = "";
                          }
                        }}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          {!minimal && (
            <div className="flex items-center gap-2 ml-auto lg:hidden">
              <Link
                href="/carrinho"
                className="relative p-2 text-gray-700 cursor-pointer transition"
                onMouseEnter={(e) => (e.currentTarget.style.color = primaryColor)}
                onMouseLeave={(e) => (e.currentTarget.style.color = "")}
                aria-label={`Carrinho (${cartCount})`}
              >
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {cartCount}
                  </span>
                )}
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-700"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          )}
        </div>

        {/* Mobile Search */}
        {showSearch && (
          <div className="md:hidden pb-3">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("header.searchPlaceholder")}
                  className="w-full h-10 pl-12 pr-4 text-base text-gray-900 bg-gray-50 border-2 border-gray-300 rounded-full focus:outline-none focus:ring-2 placeholder-gray-500"
                  style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                />
              </div>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-2">
              {loggedUser && (
                <>
                  {/* Header do usuário mobile */}
                  <div className="px-4 py-3 bg-gray-50 rounded-lg mx-3 mb-2">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${primaryColor}20` }}
                      >
                        <User className="w-5 h-5" style={{ color: primaryColor }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Olá, {loggedUser.firstName}</p>
                        <p className="text-xs text-gray-500">{loggedUser.email}</p>
                      </div>
                    </div>
                  </div>
                  <Link
                    href="/meu-aprendizado"
                    className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded mx-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <BookOpen className="w-4 h-4 text-gray-400" />
                    Meus Cursos
                  </Link>
                  <Link
                    href="/meus-dados"
                    className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded mx-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <FileText className="w-4 h-4 text-gray-400" />
                    Meus Dados
                  </Link>
                  <Link
                    href="/meu-aprendizado?tab=certificacoes"
                    className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded mx-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Award className="w-4 h-4 text-gray-400" />
                    Certificações
                  </Link>
                  <Link
                    href="/meu-aprendizado?tab=suporte"
                    className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded mx-1"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <HeadphonesIcon className="w-4 h-4 text-gray-400" />
                    Suporte
                  </Link>
                  <hr className="my-2" />
                </>
              )}
              <Link
                href="/cursos"
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
              >
                {t("header.courses")}
              </Link>
              <Link
                href="/simulador"
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
              >
                {t("header.careerSimulator")}
              </Link>
              <Link
                href="/verificar"
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
              >
                {t("header.verifyCertificate")}
              </Link>
              <hr className="my-2" />
              {loggedUser ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center gap-2 px-4 py-3 text-base font-bold text-red-600 border-2 border-red-200 rounded-md mx-3 hover:bg-red-50 cursor-pointer"
                >
                  <LogOut className="w-4 h-4" />
                  Sair da Conta
                </button>
              ) : (
                <>
                  <Link
                    href="/login"
                    className="px-4 py-3 text-base font-bold text-center text-gray-900 border-2 border-gray-300 rounded-md mx-3"
                  >
                    {t("header.login")}
                  </Link>
                  <Link
                    href="/cadastro"
                    className="px-4 py-3 text-base font-bold text-center text-white bg-gray-900 rounded-md mx-3"
                  >
                    {t("header.signup")}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
